import { Component } from 'react';
import { memoize, isEqual } from 'underscore';
import I18n from 'I18n';
import getLang from 'I18n/utils/getLang';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { userInfoSync } from 'hub-http/userInfo';
import UIFormLabel from 'UIComponents/form/UIFormLabel';
import UISelect from 'UIComponents/input/UISelect';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import UIFormControl from 'UIComponents/form/UIFormControl';
// @ts-expect-error No types yet
import withDebouncedInput from 'ContentComponents/decorators/withDebouncedInput';
import CallToActionSelect from 'ContentComponents/cmv2/url/CallToActionSelect';
import PhoneNumberSelect from 'ContentComponents/cmv2/url/PhoneNumberSelect';
import PaymentSelect from 'ContentComponents/cmv2/url/PaymentSelect';
import { getHasPublishedWebInteractive } from 'ContentData/api/WebInteractives';
import { ExternalUrlPattern, EmailAddressWithQueryParamsPattern, validateWhiteSpace } from 'ContentData/helpers/validationHelpers';
import * as FieldTypes from 'ContentUtils/constants/CustomWidgetFieldTypes';
import UrlTypes from 'ContentUtils/enums/UrlTypes';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const EmptyValidator = {
  test: () => true
};
const i18nKey = key => `ContentComponents.cmv2.fields.url.${key}`;
const DebounceTextInput = withDebouncedInput(TextInput);
const getTypeSelectUrlTypes = supportedTypes => {
  return supportedTypes.reduce((acc, urlType) => {
    if (urlType !== UrlTypes.PHONE_NUMBER && urlType !== UrlTypes.WHATSAPP_NUMBER) {
      acc.push(urlType);
    } else if (!acc.includes(UrlTypes.PHONE_NUMBER)) {
      acc.push(UrlTypes.PHONE_NUMBER);
    }
    return acc;
  }, []);
};
const getTypeOptions = memoize((lang, supportedTypes) => {
  if (!supportedTypes.length) return [];
  return getTypeSelectUrlTypes(supportedTypes).map(urlType => ({
    text: I18n.text(i18nKey(`linkType.linkLabels.${urlType}`)),
    value: urlType
  }));
}, (lang, supportedTypes) => {
  if (!(supportedTypes && supportedTypes.length)) return lang;
  return `${lang}:${supportedTypes.toString()}`;
});
class UrlField extends Component {
  constructor(props) {
    super(props);
    this.onTypeChange = this.onTypeChange.bind(this);
    this.onHrefChange = this.onHrefChange.bind(this);
    this.onContentIdChange = this.onContentIdChange.bind(this);
    this.validate = this.validate.bind(this);
    const {
      user
    } = userInfoSync();
    const hasPaymentsAccess = user.scopes.includes('payment-links-read');
    const hasWhatsappAccess = user.scopes.includes('messaging-access');
    this.state = {
      error: false,
      hasCallsToActionAccess: this.selectedUrlType === UrlTypes.CALL_TO_ACTION,
      hasPaymentsAccess,
      hasWhatsappAccess
    };
  }
  componentDidMount() {
    const {
      user
    } = userInfoSync();
    const {
      value
    } = this.props;
    if (value && value.href) {
      this.validate(value.href);
    }
    if (!this.state.hasCallsToActionAccess && user.scopes.includes('calls-to-action-read')) {
      getHasPublishedWebInteractive().then(hasCallsToActionAccess => {
        this.setState({
          hasCallsToActionAccess
        });
      }).catch(error => {
        console.error(error);
      });
    }
  }
  componentDidUpdate(prevProps) {
    /* Fix types not matching accepted types */
    const {
      supported_types: supportedTypes,
      value = {
        type: UrlTypes.EXTERNAL,
        href: ''
      }
    } = this.props;
    if (supportedTypes && supportedTypes.length && value && supportedTypes.indexOf(value.type) === -1 && !isEqual(supportedTypes, prevProps.supported_types)) {
      this.props.onFieldChange({
        content_id: null,
        href: '',
        type: supportedTypes[0]
      });
    }
  }
  get selectedUrlType() {
    const {
      value = {
        type: UrlTypes.EXTERNAL,
        href: ''
      }
    } = this.props;
    if (value && value.type) {
      return value.type === UrlTypes.WHATSAPP_NUMBER ? UrlTypes.PHONE_NUMBER : value.type;
    }
    return UrlTypes.EXTERNAL;
  }
  get supportedUrlTypes() {
    const {
      supported_types
    } = this.props;
    const {
      hasCallsToActionAccess,
      hasPaymentsAccess,
      hasWhatsappAccess
    } = this.state;
    let supportedTypes = !supported_types ? Object.keys(UrlTypes) : [...supported_types];
    if (!hasPaymentsAccess) {
      supportedTypes = supportedTypes.filter(urlType => urlType !== UrlTypes.PAYMENT);
    }
    if (!hasWhatsappAccess) {
      supportedTypes = supportedTypes.filter(urlType => urlType !== UrlTypes.WHATSAPP_NUMBER);
    }
    if (!hasCallsToActionAccess) {
      supportedTypes = supportedTypes.filter(urlType => urlType !== UrlTypes.CALL_TO_ACTION);
    }
    return supportedTypes;
  }
  get validator() {
    const {
      value = {
        type: UrlTypes.EXTERNAL,
        href: ''
      }
    } = this.props;
    switch (value && value.type) {
      case UrlTypes.EXTERNAL:
        return ExternalUrlPattern;
      case UrlTypes.EMAIL_ADDRESS:
        return EmailAddressWithQueryParamsPattern;
      default:
        return EmptyValidator;
    }
  }
  onTypeChange({
    target: {
      value: type
    }
  }) {
    const shouldSelectWhatsappNumber = type === UrlTypes.PHONE_NUMBER && !this.supportedUrlTypes.includes(UrlTypes.PHONE_NUMBER);
    if (shouldSelectWhatsappNumber) type = UrlTypes.WHATSAPP_NUMBER;
    this.props.onFieldChange({
      content_id: null,
      href: '',
      type
    });
    this.setState({
      error: false
    });
  }
  validate(href) {
    if (!href) return true;
    let isValid = false;
    if (validateWhiteSpace(href)) {
      isValid = this.validator.test(href);
    }
    this.setState({
      error: !isValid
    });
    return isValid;
  }
  onHrefChange(arg) {
    let href = arg;
    if (typeof arg === 'object' && arg) href = arg.target.value;
    this.validate(href);
    const {
      type = UrlTypes.EXTERNAL
    } = this.props.value || {};
    this.props.onFieldChange({
      content_id: null,
      href,
      type
    });
  }
  onContentIdChange({
    target: {
      value: content_id
    }
  }) {
    setTimeout(() => {
      const {
        type = UrlTypes.CONTENT
      } = this.props.value || {};
      this.props.onFieldChange({
        content_id,
        href: null,
        type
      });
    }, 0);
  }
  renderTypeSelect() {
    const {
      error: requiredError = false,
      readOnly,
      useOnDark
    } = this.props;
    const options = getTypeOptions(getLang(), this.supportedUrlTypes);
    if (options.length < 2) return null;
    return /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(UIFormLabel, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('linkType.label')
        })
      }), /*#__PURE__*/_jsx(UISelect, {
        buttonUse: useOnDark ? 'form-on-dark' : 'form',
        defaultValue: UrlTypes.EXTERNAL,
        error: requiredError,
        onChange: this.onTypeChange,
        options: options,
        readOnly: readOnly,
        value: this.selectedUrlType
      })]
    });
  }
  renderInput() {
    const {
      componentsByType,
      error: requiredError = false,
      fieldKey,
      pageContext = {},
      placeholder,
      readOnly,
      required,
      useOnDark,
      value = {
        type: UrlTypes.EXTERNAL,
        href: ''
      }
    } = this.props;
    const {
      type,
      href,
      content_id
    } = value || {};
    switch (type) {
      case UrlTypes.CONTENT:
        {
          const ComponentType = componentsByType[FieldTypes.PAGE];
          return /*#__PURE__*/_jsx(ComponentType, {
            error: requiredError,
            value: content_id,
            onChange: this.onContentIdChange,
            readOnly: readOnly,
            useOnDark: useOnDark
          });
        }
      case UrlTypes.BLOG:
        {
          const ComponentType = componentsByType[FieldTypes.BLOG];
          return /*#__PURE__*/_jsx(ComponentType, {
            error: requiredError,
            value: content_id,
            onChange: this.onContentIdChange,
            readOnly: readOnly,
            useOnDark: useOnDark
          });
        }
      case UrlTypes.FILE:
        {
          const ComponentType = componentsByType[FieldTypes.FILE];
          return /*#__PURE__*/_jsx(ComponentType, {
            error: requiredError,
            fieldKey: fieldKey,
            onFieldChange: this.onHrefChange,
            value: href,
            readOnly: readOnly,
            label: I18n.text(i18nKey(`input.labelsByType.${UrlTypes.FILE}`))
          });
        }
      case UrlTypes.CALL_TO_ACTION:
        {
          return /*#__PURE__*/_jsx(CallToActionSelect, {
            error: requiredError,
            value: content_id,
            onChange: this.onContentIdChange,
            readOnly: readOnly,
            useOnDark: useOnDark,
            pageContext: pageContext
          });
        }
      case UrlTypes.PHONE_NUMBER:
      case UrlTypes.WHATSAPP_NUMBER:
        {
          return /*#__PURE__*/_jsx(PhoneNumberSelect, {
            onChange: this.onHrefChange,
            onTypeChange: this.onTypeChange,
            readOnly: readOnly,
            supportedTypes: this.supportedUrlTypes,
            urlType: type,
            useOnDark: useOnDark,
            value: href,
            error: requiredError
          });
        }
      case UrlTypes.PAYMENT:
        {
          return /*#__PURE__*/_jsx(PaymentSelect, {
            onChange: this.onHrefChange,
            required: required,
            value: href
          });
        }
      default:
        return /*#__PURE__*/_jsx(DebounceTextInput, {
          error: requiredError,
          onChange: this.onHrefChange,
          placeholder: placeholder,
          readOnly: readOnly,
          use: useOnDark ? 'on-dark' : 'default',
          value: href,
          "data-test-id": "cmv2-link-field-external-url-input"
        });
    }
  }
  renderInputForm() {
    const {
      required,
      value = {
        type: UrlTypes.EXTERNAL,
        href: ''
      }
    } = this.props;
    const {
      error
    } = this.state;
    const {
      content_id,
      href,
      type
    } = value || {};
    const hasNoRequiredValueSet = required && !href && !content_id;
    const hideFormLabel = type === UrlTypes.FILE || type === UrlTypes.PHONE_NUMBER || type === UrlTypes.WHATSAPP_NUMBER;
    const formLabel = !hideFormLabel && /*#__PURE__*/_jsx(FormattedMessage, {
      message: i18nKey(`input.labelsByType.${this.selectedUrlType}`)
    });
    let errorMessage = null;
    if (error) {
      errorMessage = /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey(`input.validation.${this.selectedUrlType}`)
      });
    } else if (hasNoRequiredValueSet) {
      errorMessage = /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('input.validation.hrefRequired')
      });
    }
    return /*#__PURE__*/_jsx(UIFormControl, {
      error: error || hasNoRequiredValueSet,
      label: formLabel,
      validationMessage: errorMessage,
      children: this.renderInput()
    });
  }
  render() {
    return /*#__PURE__*/_jsxs("div", {
      children: [this.renderTypeSelect(), this.renderInputForm()]
    });
  }
}
export default UrlField;